import { Link, useLocation, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Users, AlertTriangle, Gift, Megaphone, Mail, FileText, Receipt, CalendarDays, Bell, LogOut, Sun, Moon, Settings, UserCog, Globe, UserPlus, Tag, ShieldCheck, Building2 } from "lucide-react";
import { GlobalSearchTrigger } from "@/components/GlobalSearch";

const items = [
  { to: "/admin", icon: LayoutDashboard, key: "dashboard" },
  { to: "/admin/associats", icon: Users, key: "associates" },
  { to: "/admin/ciutadans", icon: UserPlus, key: "citizens" },
  { to: "/admin/interessos", icon: Tag, key: "interests" },
  { to: "/admin/collaboradors", icon: Building2, key: "collaborators" },
  { to: "/admin/promocions-pendents", icon: ShieldCheck, key: "promotions_review" },
  { to: "/admin/usuaris", icon: UserCog, key: "users" },
  { to: "/admin/incidencies", icon: AlertTriangle, key: "incidents" },
  { to: "/admin/targetes-regal", icon: Gift, key: "giftcards" },
  { to: "/admin/campanyes", icon: Megaphone, key: "campaigns" },
  { to: "/admin/mailings", icon: Mail, key: "mailings" },
  { to: "/admin/documents", icon: FileText, key: "documents" },
  { to: "/admin/factures", icon: Receipt, key: "invoices" },
  { to: "/admin/agenda", icon: CalendarDays, key: "agenda" },
  { to: "/admin/notificacions", icon: Bell, key: "notifications" },
  { to: "/admin/configuracio", icon: Settings, key: "settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const loc = useLocation();
  const changeLang = (l) => { i18n.changeLanguage(l); localStorage.setItem("ss_lang", l); };

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/associat" replace />;

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden md:flex w-64 border-r border-border/60 bg-card flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-border/60">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.jpg" alt="Shopping Salou" className="w-10 h-10 rounded-xl object-cover" />
            <div>
              <div className="font-heading font-bold text-sm text-primary">Shopping Salou</div>
              <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("nav.panel_admin")}</div>
            </div>
          </Link>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {items.map((it) => {
            const active = loc.pathname === it.to || (it.to !== "/admin" && loc.pathname.startsWith(it.to));
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`admin-nav-${it.key}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  active ? "bg-primary text-primary-foreground shadow-sm" : "hover:bg-secondary text-foreground"
                }`}
              >
                <it.icon className="w-4 h-4" />
                {t(`admin.${it.key}`)}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border/60 space-y-1">
          <div className="text-xs px-3 py-1 text-muted-foreground truncate">{user.email}</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start rounded-xl" data-testid="admin-lang-switcher" aria-label={t("a11y.change_lang")}>
                <Globe className="w-4 h-4 mr-2" aria-hidden="true" /> {t("common.lang")} · <span className="ml-1 uppercase text-muted-foreground">{i18n.language}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("common.lang")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[
                { code: "ca", label: "Català" }, { code: "es", label: "Español" },
                { code: "en", label: "English" }, { code: "fr", label: "Français" },
              ].map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => changeLang(l.code)} data-testid={`admin-lang-${l.code}`}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" className="w-full justify-start rounded-xl" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="admin-theme-toggle" aria-label={t("a11y.theme_toggle")}>
            {theme === "dark" ? <Sun className="w-4 h-4 mr-2" aria-hidden="true" /> : <Moon className="w-4 h-4 mr-2" aria-hidden="true" />}
            {theme === "dark" ? t("a11y.light_mode") : t("a11y.dark_mode")}
          </Button>
          <Button variant="ghost" className="w-full justify-start rounded-xl" onClick={logout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" aria-hidden="true" /> {t("nav.logout")}
          </Button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 glass border-b border-border/60 px-4 py-2 flex items-center justify-between gap-3">
          <div className="md:hidden font-heading font-bold text-sm">{t("nav.admin")}</div>
          <div className="flex-1 hidden md:flex"><GlobalSearchTrigger /></div>
          <div className="md:hidden flex-1"><GlobalSearchTrigger compact /></div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="admin-mobile-logout" className="md:hidden">{t("nav.logout")}</Button>
        </div>
        <div className="md:hidden overflow-x-auto flex gap-2 p-3 border-b border-border/60 bg-card">
          {items.map((it) => (
            <Link key={it.to} to={it.to} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${loc.pathname === it.to || (it.to !== '/admin' && loc.pathname.startsWith(it.to)) ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
              {t(`admin.${it.key}`)}
            </Link>
          ))}
        </div>
        <main className="p-4 sm:p-8 max-w-7xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
